import { inject, injectable } from 'inversify'
import type { RouteLocation, RouteParamsRawGeneric, RouteRecordNameGeneric } from 'vue-router'
import { SERVICE_TYPES } from '@/core/container/types'
import type RouterService from '@/services/route/routerService'
import type RouterPermissionsService from '@/services/routerPermissionsService'
import type LoggerService from '@/services/loggerService'
import type { Nullable } from '@/types'

@injectable()
export class RoutePrefetchService {
  private alreadyPrefetchedIds: Set<string> = new Set()

  private alreadyPrefetchedChunks: Set<string> = new Set()

  private routeChunkMap: Map<string, Set<string>> = new Map()

  private chunkMapLoadPromise: Promise<void> | null = null

  constructor(
    @inject(SERVICE_TYPES.RouterService)
    protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.RouterPermissionsService)
    protected readonly routerPermissionsService: RouterPermissionsService,
    @inject(SERVICE_TYPES.LoggerService)
    protected readonly loggerService: LoggerService,
  ) {}

  public async prefetchChunksForRoute(
    routeName: RouteRecordNameGeneric,
    params?: RouteParamsRawGeneric,
    recursive = true,
  ): Promise<void> {
    if (typeof routeName !== 'string') {
      return
    }

    const resolvedRoute = this.resolveRoute(routeName, params)
    if (!resolvedRoute) {
      return
    }

    if (!resolvedRoute.meta?.__tmPrefetchId) {
      // There is no prefetchId, so there's nothing to prefetch. Caching this state to skip future checks.
      this.loggerService.log(
        'routePrefetch',
        `No prefetch data found for route "${routeName}". Skipping…`,
        'RoutePrefetchService.prefetchChunksForRoute',
      )
      return
    }

    if (this.alreadyPrefetchedIds.has(resolvedRoute.meta.__tmPrefetchId)) {
      return
    }

    this.loggerService.log(
      'routePrefetch',
      `Prefetching "${routeName}" with "${resolvedRoute.meta.__tmPrefetchId}" prefetchId…`,
      'RoutePrefetchService.prefetchChunksForRoute',
    )

    this.prefetchChunksForPrefetchId(resolvedRoute.meta.__tmPrefetchId)

    if (recursive) {
      // Prefetch parent routes first
      await Promise.all(
        resolvedRoute.matched.map((matchedRoute) => {
          const prefetchId = matchedRoute.meta?.__tmPrefetchId
          if (prefetchId) {
            return this.prefetchChunksForPrefetchId(prefetchId)
          }
          return Promise.resolve()
        }),
      )
    }
  }

  public async prefetchChunksForPrefetchId(prefetchId: string): Promise<void> {
    if (!this.chunkMapLoadPromise) {
      this.chunkMapLoadPromise = this.loadRouteChunkMap()
    }
    await this.chunkMapLoadPromise

    if (this.alreadyPrefetchedIds.has(prefetchId)) {
      return
    }

    const prefetchInfo = this.routeChunkMap.get(prefetchId)
    const chunksToPrefetch = prefetchInfo ?? new Set()

    this.loggerService.log(
      'routePrefetch',
      `Prefetching ${chunksToPrefetch.size} chunks for "${prefetchId}" prefetchId…`,
      'RoutePrefetchService.prefetchChunksForPrefetchId',
    )

    this.injectPrefetchLinks(chunksToPrefetch)

    this.alreadyPrefetchedIds.add(prefetchId)
  }

  protected resolveRoute(
    routeName: RouteRecordNameGeneric,
    params?: RouteParamsRawGeneric,
  ): Nullable<RouteLocation & { href: string }> {
    try {
      const resolved = this.routerService.resolveFinalPath({ name: routeName, params })
      const hasPermission = this.routerPermissionsService.isAllowAccessToRoute(resolved)
      if (!hasPermission) {
        return null
      }

      return resolved
    } catch {
      this.loggerService.log(
        'routePrefetch',
        `Failed to resolve route "${String(routeName)}" with params "${JSON.stringify(params)}"`,
        'RoutePrefetchService.resolveRoute',
      )
      return null
    }
  }

  protected async loadRouteChunkMap(): Promise<void> {
    const routeChunkMapPath = window.BUILD_MANIFEST?.routeChunkMap
    if (routeChunkMapPath) {
      // vite-ignore is preventing the bundler from transforming the import
      try {
        const module = await import(/* @vite-ignore */ routeChunkMapPath)
        this.routeChunkMap = module.ROUTE_CHUNK_MAP
      } catch {
        // eslint-disable-next-line no-console
        console.warn('Failed to load route chunk map from BUILD_MANIFEST, using an empty map as a fallback')
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('routeChunkMap was not found in BUILD_MANIFEST, using an empty map as a fallback')
    }
  }

  protected injectPrefetchLinks(chunks: Set<string>): void {
    chunks.forEach((chunkPath) => {
      if (this.alreadyPrefetchedChunks.has(chunkPath)) {
        return
      }

      const link = document.createElement('link')
      link.crossOrigin = ''
      link.href = `/${chunkPath}`
      if (chunkPath.endsWith('.css')) {
        link.rel = 'preload'
        link.as = 'style'
      } else {
        link.rel = 'modulepreload'
        link.as = 'script'
      }
      document.head.appendChild(link)

      this.alreadyPrefetchedChunks.add(chunkPath)
    })
  }
}
